import { useContext, useState, ReactNode } from "react";
import {
  Switch,
  Skeleton,
  Tooltip,
  Select,
  MenuItem,
  Stack,
  styled,
  Box,
} from "@mui/material";
import Typography from "@common/components/Typography";
import { updateRoom } from "@services/rooms";
import { RoomsRealtimeContext } from "../../Layout/context";
import { updateDevice } from "@services/devices";
import { useErrorNotification } from "@common/components/Notification";
import { useTranslation } from "react-i18next";
import TicketBadge from "@common/components/TicketBadge";
import { useLocation, useParams } from "react-router-dom";
import {
  LocationDetailContext,
  LocationDetailDispatchContext,
} from "@pages/Assets/Location/context";
import {
  AccountDetailContext,
  AccountDetailDispatchContext,
} from "@pages/Assets/Account/context";
import { canEditAccount, canEditLocation } from "@common/utils/user";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { saveLocation } from "@services/locations";
import { putAccount } from "@services/account";
import StatusChip from "@common/components/Chips/StatusChip";

export type HeaderSummaryRoomProps = {
  number?: number;
  select?: {
    tooltip?: string;
    disabled?: boolean;
    color?: "success" | "error" | "grey";
    items: { value: string; label?: string }[];
    onClick: Function;
    value: string;
  };
  status?: boolean;
  open?: boolean;
  statusUntil?: number;
  notificationsNumber?: number;
};

const StyledSelect = styled(
  ({
    select,
    children,
  }: {
    select?: HeaderSummaryRoomProps["select"];
    children?: ReactNode;
  }) => (
    <Select
      disabled={select?.disabled}
      value={select?.value}
      onChange={(event) => select?.onClick(event.target.value as string)}
    >
      {children}
    </Select>
  )
)(({ theme, select }) => {
  const { palette } = theme;
  return {
    backgroundColor:
      select?.color === "success"
        ? palette.success.main
        : select?.color === "error"
        ? palette.error.main
        : select?.color === "grey"
        ? palette.common.grey.g9B9B9B
        : "",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiSelect-select": {
      padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
    },
    borderRadius: theme.radius.medium,
    "& .MuiSelect-select, .MuiSvgIcon-root": {
      color:
        select?.color === "error" ? palette.common.white : palette.common.black,
      "&.Mui-disabled": {
        WebkitTextFillColor: "unset",
      },
    },
  };
});

const HeaderSummaryRoom = (props: HeaderSummaryRoomProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { detail, canEdit } = useContext(RoomsRealtimeContext);
  const location = useContext(LocationDetailContext);
  const { refetch: refetchLocation } = useContext(
    LocationDetailDispatchContext
  );
  const account = useContext(AccountDetailContext);
  const { refetch: refetchAccount } = useContext(AccountDetailDispatchContext);
  const [user] = useAuthenticatedUser();
  const { t } = useTranslation();
  const { deviceid, roomid, locationid, accountid, meetingid } = useParams();
  const state = useLocation().state as { previous: string; tab?: string };
  const { number, status, select, notificationsNumber, open, statusUntil } =
    props;

  const dispatchError = useErrorNotification();
  const handleOnClick = async (value: boolean) => {
    setLoading(true);
    try {
      if (detail?.room) {
        await updateRoom(detail.room.id, {
          ...detail?.room,
          inService: value,
        });
        await detail?.refetch();
      } else if (detail?.device) {
        await updateDevice(detail.device.id, {
          ...detail?.device,
          active: value,
        });
        await detail?.refetch();
      } else if (location) {
        await saveLocation({ ...location, active: value });
        await refetchLocation();
      } else if (account) {
        await putAccount({ ...account, active: value });
        await refetchAccount();
      }
    } catch (e) {
      dispatchError(e);
    }
    setLoading(false);
  };
  const isLoading = detail?.loading || loading;
  const canSwitchBeDisabled =
    canEdit === false ||
    (location !== undefined && !canEditLocation(user)) ||
    (account !== undefined && !canEditAccount(user));

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "2rem"
      }}>
      {isLoading ? (
        <Box sx={{
          width: "100%"
        }}>
          <Skeleton width="100%" height="100%" />
        </Box>
      ) : (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%"
          }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-start"
            }}>
            {select ? (
              <Tooltip title={select.tooltip ?? ""} arrow>
                <Stack direction="row" sx={{
                  alignItems: "center"
                }}>
                  <StyledSelect select={select}>
                    {select.items.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  {number && (
                    <Stack
                      direction="row"
                      sx={[{
                        alignItems: "center"
                      }, ({ palette }) => ({
                        color: palette.common.white,
                        border: `1px solid ${palette.common.white}`,
                        borderRadius: "50px",
                        marginLeft: 1,
                        px: 1.5,
                      })]}>
                      <Typography size="small">{number}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Tooltip>
            ) : (
              <Tooltip title={status ? t("Deactivate") : t("Activate")}>
                <Switch
                  checked={status}
                  disabled={canSwitchBeDisabled}
                  onChange={(_, value) => handleOnClick(value)}
                />
              </Tooltip>
            )}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center"
            }}>
            {open !== undefined ? (
              <StatusChip status={open} statusUntil={statusUntil} />
            ) : null}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end"
            }}>
            {(notificationsNumber ?? 0) > 0 && (
              <TicketBadge
                replaceHistoryRecord
                notificationsNumber={notificationsNumber}
                previousPage={state?.previous}
                {...(roomid
                  ? { roomId: roomid }
                  : deviceid
                  ? { deviceId: deviceid }
                  : accountid
                  ? { accountId: accountid }
                  : locationid
                  ? { locationId: locationid }
                  : { meetingId: meetingid })}
              />
            )}
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

export default HeaderSummaryRoom;
